import React, { useEffect, useState } from "react"
import { FormContextType, useForm } from "../../contexts/formContext"
import { Button } from "../ui/Button"
import { CircularProgress, FormControl, Grid } from "@mui/material"
import { FormInput } from "../ui/Input"
import { Box } from "@mui/system"
import { FormSelect } from "../ui/Select"
import { FormMultiSelect } from "../ui/MultiSelect"
import { useApiMutation } from "../../hooks/useApiMutation"

const OrgTypeMap = {
  "Clinic or Wellness Hub": "CLINIC_OR_HUB",
  "Individual Practitioner": "INDIVIDUAL",
  Other: "OTHER",
}

const ServiceTypeeMap = {
  "Biomarker Testing": "Biomarker_Testing",
  "Regenerative Medicine": "Regenerative_Medicine",
  "Health and Wellness Coaching": "Health_Wellness_Coaching",
  "Chiropractic, Massage, Acupuncture, or Physiotherapy":
    "Chiropractic_Massage_Acupuncture_Physiotherapy",
  "Psychologist, Psychiatrist, or Counsellor":
    "Psychologist_Psychiatrist_Counsellor",
  "Yoga, Breathwork, or Meditation": "Yoga_Breathwork_Meditation",
  Other: "OTHER",
}

interface Props {
  onComplete?: () => void
}
const ProOrgDetailsForm = ({ onComplete }: Props) => {
  const { formState, formActions } = useForm() as FormContextType
  const [errorMessage, setErrorMessage] = useState("")
  const [showLoading, setShowLoading] = useState<boolean>(false)

  const [
    updateProfessionalConfiguration,
    { loading, error, data, errMsg },
  ] = useApiMutation("account", "UpdateProfessionalConfiguration")

  const submitHandler = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault()

    if (!formActions.isValid()) {
      return
    }
    setShowLoading(true)

    updateProfessionalConfiguration({
      variables: {
        input: {
          organizationName: formState.inputs.organizationName.value,
          organizationType: OrgTypeMap[formState.inputs.organizationType.value],
          ...(formState.inputs.organizationTypeOther.value
            ? {
                organizationOtherType:
                  formState.inputs.organizationTypeOther.value,
              }
            : {}),
          services: (formState.inputs.services.value as string[]).map(
            s => ServiceTypeeMap[s]
          ),
          ...(formState.inputs.servicesTypeOther.value
            ? { servicesOther: formState.inputs.servicesTypeOther.value }
            : {}),
        },
      },
    })
  }

  useEffect(() => {
    if (error) {
      setErrorMessage("Sorry we experienced an error. Please try again later.")
      setShowLoading(false)
    }
  }, [error])

  useEffect(() => {
    if (data && onComplete) {
      onComplete()
    }
  }, [data])

  return (
    <>
      <form
        onSubmit={submitHandler}
        noValidate
        data-testid={"organiation-details-form"}
      >
        <Grid container spacing={2}>
          <Grid item xs={6}>
            <FormControl variant="standard" fullWidth>
              <FormInput
                fieldId="org-name"
                fieldName="organizationName"
                inputs={formState.inputs}
                inputHandler={formActions.inputHandler}
              />
            </FormControl>
          </Grid>
          <Grid item xs={6}>
            <FormControl variant="standard" fullWidth>
              <FormSelect
                fieldId="org-type"
                fieldName={"organizationType"}
                inputs={formState.inputs}
                inputHandler={formActions.inputHandler}
                values={Object.keys(OrgTypeMap)}
                autoComplete="off"
              />
            </FormControl>
          </Grid>
          {formState.inputs.organizationType.value === "Other" && (
            <Grid item xs={12}>
              <FormControl variant="standard" fullWidth>
                <FormInput
                  fieldId="org-type-other"
                  fieldName="organizationTypeOther"
                  inputs={formState.inputs}
                  inputHandler={formActions.inputHandler}
                />
              </FormControl>
            </Grid>
          )}
          <Grid item xs={12}>
            <FormControl variant="standard" fullWidth>
              <FormMultiSelect
                fieldId="org-services"
                fieldName={"services"}
                fullLabel={"What services do you offer? "}
                inputs={formState.inputs}
                inputHandler={formActions.inputHandler}
                values={Object.keys(ServiceTypeeMap)}
              />
            </FormControl>
          </Grid>
          {formState.inputs.services.value.includes("Other") && (
            <Grid item xs={12}>
              <FormControl variant="standard" fullWidth>
                <FormInput
                  fieldId="org-services-other"
                  fieldName="servicesTypeOther"
                  inputs={formState.inputs}
                  inputHandler={formActions.inputHandler}
                />
              </FormControl>
            </Grid>
          )}
          <Grid item xs={12}>
            <Box sx={{ display: "flex", justifyContent: "center" }}>
              <Button
                type="primary"
                submit={true}
                id={"send-details"}
                sectionId="org-detail"
                title={"NEXT"}
                disabled={loading || showLoading}
                testId={"submit"}
              />
            </Box>
          </Grid>
        </Grid>

        {error && (
          <Box sx={{ marginTop: "1rem", textAlign: "center" }}>
            {
              <p id="user-update-error" className="error">
                {errorMessage}
              </p>
            }
          </Box>
        )}
        {(loading || showLoading) && (
          <Box sx={{ marginTop: "1rem", textAlign: "center" }}>
            <CircularProgress />
          </Box>
        )}
      </form>
    </>
  )
}

export default ProOrgDetailsForm
